import React, { useState } from 'react';
import { X, Globe, Bell, Save, Zap, Type, Moon, Shield, Lock, Eye } from 'lucide-react';
import './SettingsModal.css';

interface AppSettings {
    language: string;
    notifications: boolean;
    autosave: boolean;
    performanceMode: 'speed' | 'accuracy';
    fontSize: number;
    contrast: 'standard' | 'high';
    storage: 'local' | 'sync';
    schoolMode: boolean;
    schoolModePin?: string;
}

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    settings: AppSettings;
    onSave: (settings: AppSettings) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, settings, onSave }: SettingsModalProps) => {
    const [localSettings, setLocalSettings] = useState<AppSettings>(settings);
    const [activeTab, setActiveTab] = useState<'general' | 'performance' | 'accessibility' | 'security'>('general');
    const [showPinInput, setShowPinInput] = useState(false);
    const [pinEntry, setPinEntry] = useState('');
    const [newPin, setNewPin] = useState('');

    if (!isOpen) return null;

    const handleToggleSchoolMode = () => {
        if (!localSettings.schoolMode) {
            // Enabling school mode - need a new PIN
            setShowPinInput(true);
        } else {
            // Disabling school mode - need existing PIN
            setShowPinInput(true);
        }
    };

    const confirmPinAction = () => {
        if (!localSettings.schoolMode) {
            // Enabling
            if (newPin.length === 4) {
                setLocalSettings({
                    ...localSettings,
                    schoolMode: true,
                    schoolModePin: newPin
                });
                setShowPinInput(false);
                setNewPin('');
            }
        } else {
            // Disabling
            if (pinEntry === localSettings.schoolModePin) {
                setLocalSettings({ ...localSettings, schoolMode: false });
                setShowPinInput(false);
                setPinEntry('');
            } else {
                alert('Incorrect PIN');
            }
        }
    };

    const handleSave = () => {
        onSave(localSettings);
        onClose();
    };

    return (
        <div className="settings-overlay animate-fade-in" onClick={onClose}>
            <div className="settings-modal animate-slide-up" onClick={e => e.stopPropagation()}>
                <div className="settings-sidebar">
                    <div className="settings-sidebar-header">
                        <h3>Settings</h3>
                    </div>
                    <div className="settings-tabs">
                        <button
                            className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
                            onClick={() => setActiveTab('general')}
                        >
                            <Globe size={18} /> General
                        </button>
                        <button
                            className={`settings-tab ${activeTab === 'performance' ? 'active' : ''}`}
                            onClick={() => setActiveTab('performance')}
                        >
                            <Zap size={18} /> Performance
                        </button>
                        <button
                            className={`settings-tab ${activeTab === 'accessibility' ? 'active' : ''}`}
                            onClick={() => setActiveTab('accessibility')}
                        >
                            <Type size={18} /> Accessibility
                        </button>
                        <button
                            className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
                            onClick={() => setActiveTab('security')}
                        >
                            <Shield size={18} /> AI & Security
                        </button>
                    </div>
                </div>

                <div className="settings-content">
                    <div className="settings-header">
                        <h4>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Settings</h4>
                        <button className="close-btn" onClick={onClose}><X size={20} /></button>
                    </div>

                    <div className="settings-body">
                        {activeTab === 'general' && (
                            <div className="settings-section">
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <label>Language & Region</label>
                                        <span>Preferred interface language</span>
                                    </div>
                                    <select
                                        value={localSettings.language}
                                        onChange={e => setLocalSettings({ ...localSettings, language: e.target.value })}
                                    >
                                        <option value="en-US">English (US)</option>
                                        <option value="en-GB">English (UK)</option>
                                        <option value="hi-IN">Hindi</option>
                                    </select>
                                </div>

                                <div className="setting-item">
                                    <div className="setting-info">
                                        <label>Notifications</label>
                                        <span>Alerts for updates & errors</span>
                                    </div>
                                    <div className={`toggle ${localSettings.notifications ? 'on' : ''}`} onClick={() => setLocalSettings({ ...localSettings, notifications: !localSettings.notifications })}>
                                        <div className="toggle-handle"></div>
                                    </div>
                                </div>

                                <div className="setting-item">
                                    <div className="setting-info">
                                        <label>Autosave</label>
                                        <span>Automatically save code changes</span>
                                    </div>
                                    <div className={`toggle ${localSettings.autosave ? 'on' : ''}`} onClick={() => setLocalSettings({ ...localSettings, autosave: !localSettings.autosave })}>
                                        <div className="toggle-handle"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'performance' && (
                            <div className="settings-section">
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <label>Optimization Mode</label>
                                        <span>Balanced speed vs. accuracy</span>
                                    </div>
                                    <div className="radio-group">
                                        <button
                                            className={localSettings.performanceMode === 'speed' ? 'active' : ''}
                                            onClick={() => setLocalSettings({ ...localSettings, performanceMode: 'speed' })}
                                        >
                                            Speed
                                        </button>
                                        <button
                                            className={localSettings.performanceMode === 'accuracy' ? 'active' : ''}
                                            onClick={() => setLocalSettings({ ...localSettings, performanceMode: 'accuracy' })}
                                        >
                                            Accuracy
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'accessibility' && (
                            <div className="settings-section">
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <label>Editor Font Size</label>
                                        <span>Adjust for readability</span>
                                    </div>
                                    <div className="counter">
                                        <button onClick={() => setLocalSettings({ ...localSettings, fontSize: Math.max(12, localSettings.fontSize - 1) })}>-</button>
                                        <span>{localSettings.fontSize}px</span>
                                        <button onClick={() => setLocalSettings({ ...localSettings, fontSize: Math.min(24, localSettings.fontSize + 1) })}>+</button>
                                    </div>
                                </div>

                                <div className="setting-item">
                                    <div className="setting-info">
                                        <label>High Contrast</label>
                                        <span>Enhanced visibility mode</span>
                                    </div>
                                    <div className={`toggle ${localSettings.contrast === 'high' ? 'on' : ''}`} onClick={() => setLocalSettings({ ...localSettings, contrast: localSettings.contrast === 'high' ? 'standard' : 'high' })}>
                                        <div className="toggle-handle"></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'security' && (
                            <div className="settings-section">
                                <div className="setting-item highlight">
                                    <div className="setting-info">
                                        <label>School Mode (AI Disable)</label>
                                        <span>Disable AI features for focused learning</span>
                                    </div>
                                    <div className={`toggle ${localSettings.schoolMode ? 'on' : ''}`} onClick={handleToggleSchoolMode}>
                                        <div className="toggle-handle"></div>
                                    </div>
                                </div>

                                {showPinInput && (
                                    <div className="pin-prompt animate-slide-up">
                                        <Lock size={24} className="pin-icon" />
                                        <label>{localSettings.schoolMode ? 'Enter PIN to Disable' : 'Set a 4-digit PIN to Enable'}</label>
                                        {!localSettings.schoolMode ? (
                                            <input
                                                type="password"
                                                maxLength={4}
                                                placeholder="New PIN"
                                                value={newPin}
                                                onChange={e => setNewPin(e.target.value.replace(/[^0-9]/g, ''))}
                                            />
                                        ) : (
                                            <input
                                                type="password"
                                                maxLength={4}
                                                placeholder="Enter PIN"
                                                value={pinEntry}
                                                onChange={e => setPinEntry(e.target.value.replace(/[^0-9]/g, ''))}
                                            />
                                        )}
                                        <div className="pin-actions">
                                            <button onClick={() => setShowPinInput(false)}>Cancel</button>
                                            <button className="confirm-btn" onClick={confirmPinAction}>Confirm</button>
                                        </div>
                                    </div>
                                )}

                                <div className="setting-info-box">
                                    <Shield size={16} />
                                    <p>When School Mode is enabled, AI code generation and error simplification are disabled. You'll need your PIN to turn them back on.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="settings-footer">
                        <button className="cancel-footer-btn" onClick={onClose}>Cancel</button>
                        <button className="save-footer-btn" onClick={handleSave}>Save Changes</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;
